import React, { useEffect, useState, useCallback } from "react";
import {
  BarChart3,
  LineChart,
  MapPin,
  Table,
  TrendingUp,
  Users,
  PieChart,
  ChevronLeft,
} from "lucide-react";

export interface ChartIndexItem {
  id: string;
  label: string;
  icon: React.ElementType;
}

const salesCharts: ChartIndexItem[] = [
  {
    id: "total-sales-new-vs-existing",
    label: "Total Sales New vs. Existing",
    icon: BarChart3,
  },
  {
    id: "total-sales-by-year-month",
    label: "Total Sales by Year & Mo...",
    icon: BarChart3,
  },
  {
    id: "total-sales-by-zip-code",
    label: "Total Sales by ZIP Code",
    icon: MapPin,
  },
  {
    id: "total-sales-new-customer-by-zip",
    label: "Total Sales New Customer by ZIP Code & Year",
    icon: MapPin,
  },
];

const customerCharts: ChartIndexItem[] = [
  {
    id: "revenue-by-invoice-age",
    label: "Revenue by Invoice Age",
    icon: BarChart3,
  },
  {
    id: "lifetime-value-by-tier",
    label: "LTV by Tier",
    icon: BarChart3,
  },
  {
    id: "lifetime-value-by-tier-table",
    label: "LTV by Tier (Table)",
    icon: Table,
  },
  {
    id: "pct-new-customers-by-zip",
    label: "% New Customers by ZIP",
    icon: PieChart,
  },
  {
    id: "pct-new-customers-change-table",
    label: "% New Customers Change",
    icon: Table,
  },
  {
    id: "avg-invoice-comparison",
    label: "Avg Invoice Comparison",
    icon: LineChart,
  },
  {
    id: "new-customer-replacement",
    label: "New Customer Replacement Efficiency",
    icon: TrendingUp,
  },
  {
    id: "existing-customer-replacement",
    label: "Existing Customer Replacement Efficiency",
    icon: Users,
  },
];

const reviewCharts: ChartIndexItem[] = [
  {
    id: "total-sales-by-year-month",
    label: "Total Sales by Year & Month",
    icon: BarChart3,
  },
  {
    id: "revenue-by-invoice-age",
    label: "Revenue by Invoice Age",
    icon: BarChart3,
  },
  {
    id: "total-sales-by-zip-code",
    label: "Total Sales by ZIP Code",
    icon: MapPin,
  },
  {
    id: "total-households-by-zip",
    label: "Total Households by ZIP",
    icon: MapPin,
  },
  {
    id: "pct-new-customers-by-zip",
    label: "% New Customers by ZIP",
    icon: PieChart,
  },
  {
    id: "avg-invoice-comparison",
    label: "Avg Invoice Comparison",
    icon: LineChart,
  },
  {
    id: "new-customer-replacement",
    label: "New Customer Replacement Efficiency",
    icon: TrendingUp,
  },
  {
    id: "existing-customer-replacement",
    label: "Existing Customer Replacement Efficiency",
    icon: Users,
  },
  {
    id: "lifetime-value-by-tier",
    label: "LTV by Tier",
    icon: BarChart3,
  },
];

export const chartIndexByTab = {
  sales: salesCharts,
  customers: customerCharts,
  review: reviewCharts,
};

type TabType = "sales" | "customers" | "review";

interface ChartIndexProps {
  activeTab: TabType;
  /**
   * Ordered chart ids actually rendered on the page (stage-curated,
   * #9292). When provided, the index lists exactly these, in this order;
   * omitted = the tab's full historical list.
   */
  visibleChartIds?: string[];
}

export const ChartIndex: React.FC<ChartIndexProps> = ({
  activeTab,
  visibleChartIds,
}) => {
  const [activeChartId, setActiveChartId] = useState<string>("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [stickyTop, setStickyTop] = useState(200);
  const tabCharts = chartIndexByTab[activeTab];
  const charts =
    visibleChartIds === undefined
      ? tabCharts
      : visibleChartIds
          .map((id) => tabCharts.find((chart) => chart.id === id))
          .filter((chart): chart is ChartIndexItem => chart !== undefined);

  const getScrollContainer = useCallback(() => {
    return document.getElementById("main-scroll-area") || window;
  }, []);

  useEffect(() => {
    const stickyHeader = document.querySelector<HTMLElement>(".sticky.top-12");
    const measure = () => {
      if (stickyHeader) {
        setStickyTop(48 + stickyHeader.offsetHeight + 8);
      }
    };
    measure();
    let observer: ResizeObserver | undefined;
    if (stickyHeader) {
      observer = new ResizeObserver(measure);
      observer.observe(stickyHeader);
    }
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [activeTab]);

  const handleScroll = useCallback(() => {
    const container = document.getElementById("main-scroll-area");
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    // The cutoff line: halfway between the sticky header bottom and the viewport bottom
    const cutoff =
      containerRect.top + stickyTop + (containerRect.height - stickyTop) * 0.4;
    let active = "";
    for (const chart of charts) {
      const el = document.getElementById(chart.id);
      if (!el) continue;
      // Active = last chart whose title is above the cutoff line
      if (el.getBoundingClientRect().top <= cutoff) {
        active = chart.id;
      }
    }
    // At the very bottom, the final chart's title may never be able to
    // reach the cutoff (short pages) — bottom of the container always
    // means the last chart is the one being read.
    const isScrollable = container.scrollHeight > container.clientHeight + 2;
    const atBottom =
      isScrollable &&
      container.scrollTop + container.clientHeight >=
        container.scrollHeight - 2;
    if (atBottom && charts.length > 0) {
      active = charts[charts.length - 1].id;
    }
    if (!active && charts.length > 0) {
      active = charts[0].id;
    }
    setActiveChartId(active);
  }, [charts, stickyTop]);

  useEffect(() => {
    const container = getScrollContainer();
    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll, getScrollContainer]);

  const scrollToChart = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActiveChartId(id);
    const container = document.getElementById("main-scroll-area");
    if (!container) return;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const visibleTop = containerRect.top + stickyTop + 8;
    const visibleBottom = containerRect.top + containerRect.height * 0.5;
    // Only scroll if the chart title isn't in the top half of the viewport
    if (elRect.top < visibleTop || elRect.top > visibleBottom) {
      const offset = elRect.top - containerRect.top;
      container.scrollTo({
        top: container.scrollTop + offset - stickyTop,
        behavior: "smooth",
      });
    }
  };

  if (isCollapsed) {
    return (
      <div
        className="sticky self-start hidden lg:block"
        style={{ top: stickyTop }}
      >
        <button
          className="p-1.5 rounded-full border border-gray-200 dark:border-slate-700 !bg-white dark:!bg-slate-800 shadow-sm text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          onClick={() => setIsCollapsed(false)}
          title="Show chart index"
        >
          <ChevronLeft className="h-4 w-4 rotate-180" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="sticky self-start w-48 flex-shrink-0 hidden lg:block rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 z-[1] cursor-pointer"
      style={{ top: stickyTop }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-300 tracking-wide">
          Chart Index
        </span>
        <button
          className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          onClick={() => setIsCollapsed(true)}
          title="Collapse chart index"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      </div>
      <nav className="space-y-0.5">
        {charts.map((chart) => {
          const Icon = chart.icon;
          const isActive = activeChartId === chart.id;
          return (
            <button
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs transition-colors cursor-pointer ${
                isActive
                  ? "bg-primary/10 text-primary dark:bg-primary/50 dark:text-white font-medium"
                  : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800"
              }`}
              key={chart.id}
              onClick={() => scrollToChart(chart.id)}
              title={chart.label}
            >
              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{chart.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};
